/**
 * Client-side helpers for Schedule → Model P6 colouring.
 * Mirrors src/services/schedule4dProgress.js planned / status rules.
 */

export const LEGEND_FALLBACK = [
  { key: 'complete', label: 'Complete', hex: '#16a34a' },
  { key: 'in_progress', label: 'In progress', hex: '#f59e0b' },
  { key: 'not_started', label: 'Not started', hex: '#94a3b8' },
  { key: 'late', label: 'Late vs planned finish', hex: '#dc2626' },
  { key: 'unmapped', label: 'In model, no Activity ID match', hex: '#7c3aed' },
];

export function hexToVector4(hex, alpha = 0.85) {
  const h = String(hex || '#94a3b8').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  return { r, g, b, a: alpha };
}

export function plannedColorAt(activity, asOfDate) {
  const start = activity.plannedStart || null;
  const finish = activity.plannedFinish || null;
  if (!asOfDate || !start) return 'not_started';
  if (start > asOfDate) return 'not_started';
  if (finish && finish <= asOfDate) return 'complete';
  return 'in_progress';
}

export function colorKeyForMode(activity, mode, asOfDate) {
  if (mode === 'planned') return plannedColorAt(activity, asOfDate);
  return activity.colorKey || 'not_started';
}

export function searchViewer(viewer, text, attributeNames) {
  return new Promise((resolve) => {
    if (!viewer || !text) {
      resolve([]);
      return;
    }
    const done = (ids) => resolve(Array.isArray(ids) ? ids : []);
    try {
      if (attributeNames?.length) {
        viewer.search(text, done, () => done([]), attributeNames, { searchHidden: true });
      } else {
        viewer.search(text, done, () => done([]), null, { searchHidden: true });
      }
    } catch {
      done([]);
    }
  });
}

export function hydrateActivityMap(obj) {
  const byActivity = new Map();
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return byActivity;
  for (const [id, ids] of Object.entries(obj)) {
    if (!id) continue;
    const unique = Array.isArray(ids)
      ? [...new Set(ids.map(Number).filter((n) => Number.isFinite(n)))]
      : [];
    byActivity.set(id, unique);
  }
  return byActivity;
}

export function serializeActivityMap(byActivity) {
  const out = {};
  for (const [id, ids] of byActivity || []) {
    if (!id) continue;
    out[id] = Array.isArray(ids) ? ids : [];
  }
  return out;
}

export function countActivityMap(byActivity) {
  let mapped = 0;
  let unmapped = 0;
  if (!byActivity || typeof byActivity.forEach !== 'function') {
    return { mapped: 0, unmapped: 0, activityCount: 0 };
  }
  byActivity.forEach((ids) => {
    if (ids && ids.length) mapped += 1;
    else unmapped += 1;
  });
  return { mapped, unmapped, activityCount: byActivity.size };
}

export function missingActivities(activities, byActivity) {
  return (activities || []).filter((a) => a.activityId && !byActivity.has(a.activityId));
}

/**
 * Search the APS viewer for each Activity ID.
 * Pass `into` + skipExisting to resume a saved map instead of starting at zero.
 */
export async function mapActivitiesToDbIds(viewer, activities, propertyNames, onProgress, options = {}) {
  const byActivity = options.into instanceof Map ? options.into : new Map();
  const list = (activities || []).filter((a) => a.activityId);
  const toScan = options.skipExisting === false
    ? list
    : list.filter((a) => !byActivity.has(a.activityId));
  const total = options.progressTotal != null ? options.progressTotal : (toScan.length + (list.length - toScan.length));
  const already = total - toScan.length;
  const partialEvery = Number(options.partialEvery) > 0 ? Number(options.partialEvery) : 0;

  for (let i = 0; i < toScan.length; i += 1) {
    const act = toScan[i];
    let ids = await searchViewer(viewer, act.activityId, propertyNames);
    if (!ids.length) ids = await searchViewer(viewer, act.activityId, null);
    const unique = [...new Set(ids.map(Number).filter((n) => Number.isFinite(n)))];
    byActivity.set(act.activityId, unique);
    if (onProgress) onProgress(already + i + 1, total, act.activityId);
    if (partialEvery && options.onPartial && ((i + 1) % partialEvery === 0)) {
      await options.onPartial(byActivity, false);
    }
  }
  return byActivity;
}

export function applyTheming(viewer, THREE, activities, byActivity, legend, mode, asOfDate) {
  if (!viewer?.model || !THREE) return { mapped: 0, unmapped: 0 };
  const legendMap = new Map((legend || LEGEND_FALLBACK).map((l) => [l.key, l.hex]));
  viewer.clearThemingColors(viewer.model);

  let mapped = 0;
  let unmapped = 0;
  const painted = new Set();

  for (const act of activities || []) {
    const ids = byActivity.get(act.activityId) || [];
    if (!ids.length) {
      unmapped += 1;
      continue;
    }
    mapped += 1;
    const key = colorKeyForMode(act, mode, asOfDate);
    const hex = legendMap.get(key) || '#94a3b8';
    const c = hexToVector4(hex, 0.88);
    const color = new THREE.Vector4(c.r, c.g, c.b, c.a);
    for (const dbId of ids) {
      painted.add(dbId);
      viewer.setThemingColor(dbId, color, viewer.model, true);
    }
  }

  return { mapped, unmapped, paintedCount: painted.size };
}
